import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Divider,
  Link,
} from '@mui/material';

const Section = ({ id, title, children }) => (
  <Box id={id} sx={{ mb: 4 }}>
    <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: 'text.primary' }}>
      {title}
    </Typography>
    {children}
  </Box>
);

const RefundPolicy = () => {
  return (
    <>
      {/* Header */}
      <Box sx={{ bgcolor: 'background.paper', pt: { xs: 8, md: 10 }, pb: 4, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Container maxWidth="md">
          <Typography component="h1" variant="h3" gutterBottom sx={{ fontWeight: 700 }}>
            Refund and Cancellation Policy
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Last Updated: 01 August 2026
          </Typography>
        </Container>
      </Box>

      {/* Body */}
      <Container maxWidth="md" sx={{ py: { xs: 5, md: 7 } }}>
        {/* Intro */}
        <Typography variant="body1" paragraph>
          This Refund and Cancellation Policy ("<strong>Policy</strong>") applies to all payments made on <strong>InfHire</strong> (www.infhire.com) (the "<strong>Platform</strong>"), operated by <strong>Creativecrewz</strong>, having its registered office at 6-3-240/6/1, 1st Floor, Sarada Street, Road D-block, Maheswari Towers, Prem Nagar Road adj. Lane, Banjara Hills, Hyderabad, Telangana 500004, India ("<strong>Company</strong>", "<strong>we</strong>", "<strong>us</strong>", "<strong>our</strong>").
        </Typography>
        <Typography variant="body1" paragraph>
          This Policy should be read together with our{' '}
          <Link component={RouterLink} to="/terms" color="primary">Terms and Conditions</Link>
          {' '}and{' '}
          <Link component={RouterLink} to="/privacy" color="primary">Privacy Policy</Link>.
          {' '}It applies to Brands/advertisers ("<strong>Brands</strong>") and content creators/influencers ("<strong>Influencers</strong>"), collectively "<strong>Users</strong>".
        </Typography>

        <Divider sx={{ my: 4 }} />

        <Section id="general" title="1. General Principle">
          <Typography variant="body1" component="div" sx={{ lineHeight: 2 }}>
            1.1 As InfHire is an intermediary platform that facilitates collaborations between Brands and Influencers, refunds are assessed based on the stage of the campaign, the nature of the payment (platform fee, campaign escrow, or subscription), and the reason for the refund request.<br />
            1.2 All refund requests are subject to review and approval by the Company, acting reasonably and in good faith.
          </Typography>
        </Section>

        <Divider sx={{ my: 4 }} />

        <Section id="subscription" title="2. Platform Subscription / Membership Fees">
          <Typography variant="body1" component="div" sx={{ lineHeight: 2 }}>
            2.1 Subscription or membership fees paid to access Platform features are <strong>non-refundable</strong> once the subscription period has commenced, except where required under applicable consumer protection law.<br />
            2.2 If a technical error results in a duplicate or incorrect charge, the affected User may request a refund within <strong>7 days</strong> of the transaction by writing to{' '}
            <Link href="mailto:contact@infhire.com" color="primary">contact@infhire.com</Link>
            {' '}with supporting evidence (transaction ID, payment screenshot).
          </Typography>
        </Section>

        <Divider sx={{ my: 4 }} />

        <Section id="escrow" title="3. Campaign Escrow Payments (Brand to Influencer)">
          <Typography variant="body1" paragraph>
            3.1 Where a Brand deposits campaign funds with the Platform to be released to an Influencer upon completion of agreed deliverables:
          </Typography>
          <Typography variant="body1" component="ul" sx={{ pl: 2, mb: 2 }}>
            <li><strong>Before Influencer Acceptance:</strong> If an Influencer has not accepted/started work on a campaign, the Brand may cancel and receive a <strong>full refund</strong>, less any non-refundable payment gateway charges.</li>
            <li><strong>After Acceptance, Before Delivery:</strong> If the Influencer has accepted the campaign but not yet submitted deliverables within the agreed timeline, the Brand may request cancellation. Refund eligibility will depend on work-in-progress already undertaken by the Influencer, as reasonably determined by the Company.</li>
            <li><strong>After Delivery/Approval:</strong> Once content has been delivered and approved by the Brand (or deemed approved after the review period specified in the campaign brief), payment to the Influencer becomes <strong>final and non-refundable</strong>, except in cases of proven fraud, plagiarism, or material breach by the Influencer.</li>
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 2 }}>
            3.2 If an Influencer fails to deliver agreed content within the timeline without valid reason, the Brand is entitled to a <strong>full refund</strong> of the escrowed amount for that deliverable.<br />
            3.3 Disputes regarding quality or scope of deliverables will be resolved per the Grievance Redressal mechanism in Section 6, and, if unresolved, per the{' '}
            <Link component={RouterLink} to="/terms#dispute-resolution" color="primary">Dispute Resolution clause in our Terms and Conditions</Link>.
          </Typography>
        </Section>

        <Divider sx={{ my: 4 }} />

        <Section id="payouts" title="4. Influencer Payouts">
          <Typography variant="body1" component="div" sx={{ lineHeight: 2 }}>
            4.1 Once a Brand approves deliverables and releases payment, the corresponding payout to the Influencer will be processed within <strong>30 days</strong>, subject to successful KYC verification.<br />
            4.2 Payouts already disbursed to an Influencer's bank account/UPI are <strong>not reversible</strong> by the Company except in cases of proven fraud, in which case recovery will be pursued as per applicable law.
          </Typography>
        </Section>

        <Divider sx={{ my: 4 }} />

        <Section id="non-refundable" title="5. Non-Refundable Circumstances">
          <Typography variant="body1" paragraph>Refunds will <strong>not</strong> be provided where:</Typography>
          <Typography variant="body1" component="ul" sx={{ pl: 2 }}>
            <li>The User violated the Terms and Conditions (e.g., fake engagement, prohibited conduct);</li>
            <li>The campaign was cancelled after content was delivered and approved;</li>
            <li>The refund request is made after the applicable request window has lapsed;</li>
            <li>The dissatisfaction relates to subjective creative preferences already accounted for in the agreed brief and revision rounds.</li>
          </Typography>
        </Section>

        <Divider sx={{ my: 4 }} />

        <Section id="how-to-request" title="6. How to Request a Refund">
          <Typography variant="body1" paragraph>
            6.1 To request a refund, contact us at{' '}
            <Link href="mailto:contact@infhire.com" color="primary">contact@infhire.com</Link>
            {' '}with:
          </Typography>
          <Typography variant="body1" component="ul" sx={{ pl: 2, mb: 2 }}>
            <li>Registered account email/User ID</li>
            <li>Transaction ID/campaign reference number</li>
            <li>Reason for the refund request</li>
            <li>Supporting documents/screenshots, if applicable</li>
          </Typography>
          <Typography variant="body1">
            6.2 We will acknowledge your request within <strong>48 hours</strong> and communicate a resolution within <strong>15 business days</strong>.
          </Typography>
        </Section>

        <Divider sx={{ my: 4 }} />

        <Section id="mode-of-refund" title="7. Mode of Refund">
          <Typography variant="body1">
            Approved refunds will be processed to the original payment method used, within <strong>7–14 business days</strong> of approval, subject to processing timelines of the respective bank/payment gateway. The Company is not responsible for delays caused by banks or third-party payment processors.
          </Typography>
        </Section>

        <Divider sx={{ my: 4 }} />

        <Section id="grievance-officer" title="8. Grievance Officer">
          <Typography variant="body1" paragraph>For unresolved refund disputes, you may escalate to our Grievance Officer:</Typography>
          <Typography variant="body1" component="div" sx={{ lineHeight: 2 }}>
            <strong>Name:</strong> Ramu Vemula<br />
            <strong>Email:</strong>{' '}<Link href="mailto:contact@infhire.com" color="primary">contact@infhire.com</Link><br />
            <strong>Address:</strong> 6-3-240/6/1, 1st Floor, Sarada Street, Road D-block, Maheswari Towers, Prem Nagar Road adj. Lane, Banjara Hills, Hyderabad, Telangana 500004, India
          </Typography>
        </Section>

        <Divider sx={{ my: 4 }} />

        <Section id="changes" title="9. Changes to this Policy">
          <Typography variant="body1">
            We may revise this Policy from time to time. Updates will be posted on this page with a revised "Last Updated" date. Continued use of the Platform after such changes constitutes acceptance of the revised Policy.
          </Typography>
        </Section>

        <Divider sx={{ my: 4 }} />

        <Section id="governing-law" title="10. Governing Law">
          <Typography variant="body1">
            This Policy is governed by the laws of India, and courts at Hyderabad, Telangana shall have exclusive jurisdiction over any disputes arising herefrom, subject to the{' '}
            <Link component={RouterLink} to="/terms#dispute-resolution" color="primary">arbitration clause in our Terms and Conditions</Link>.
          </Typography>
        </Section>

        <Divider sx={{ my: 4 }} />

        {/* Related policies */}
        <Box sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 3 }}>
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>Related Policies</Typography>
          <Link component={RouterLink} to="/terms" color="primary" display="block" sx={{ mb: 0.5 }}>
            Terms and Conditions
          </Link>
          <Link component={RouterLink} to="/privacy" color="primary" display="block">
            Privacy Policy
          </Link>
        </Box>
      </Container>
    </>
  );
};

export default RefundPolicy;
